import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Center, Text, SimpleGrid, useTheme } from '@wegroup/design-system';
import SingleOffer from './SingleOffer';
import CheckMark from '../../../../common/src/components/CheckMark';
import { IconActionClose } from 'wg-fe-ui';
import { ProductWithQuality } from '../../../../../types/Quotes';
import { useIsFetching } from 'react-query';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import LoadingScreen from '../../../../common/src/components/LoadingScreen';
import { getProductKey } from '../../services/offersService';
import { isSocialLoginUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';

interface Props {
  shownProductsList: ProductWithQuality[];
  // Temorary prop drilling, there is another task to improve "OffersView" which will remove the following props
  riskObject: string;
  addToSelectedOffers: (arg0: string) => void;
  removeFromSelectedOffers: (id?: string) => void;
}

const OffersContainerContent: React.FC<React.PropsWithChildren<Props>> = ({
  shownProductsList,
  riskObject,
  addToSelectedOffers,
  removeFromSelectedOffers,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const sessionId = useSessionId();

  const initialRender = useRef<boolean>(true);
  const hasLoadedOnce = useRef<boolean>(false);

  const isFetchingQuote = useIsFetching([sessionId, 'quotes', riskObject]);

  const isLoading =
    isSocialLoginUrl && isFetchingQuote && !hasLoadedOnce.current;

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else if (isFetchingQuote === 0 && !hasLoadedOnce.current) {
      hasLoadedOnce.current = true;
    }
  }, [isFetchingQuote]);

  return (
    <SimpleGrid w="100%" gridGap={{ base: 4, laptop: 12 }}>
      {shownProductsList.length > 0 ? (
        <>
          {shownProductsList.map((product) => (
            <SingleOffer
              // A key has to be identified by the insurance company as this will not change
              // The hash_id can change, making it a bad identifier
              key={getProductKey(product)}
              product={product}
              riskObject={riskObject}
              addToSelectedOffers={addToSelectedOffers}
              removeFromSelectedOffers={removeFromSelectedOffers}
            />
          ))}
          {isLoading ? (
            <LoadingScreen />
          ) : (
            <Center
              mx={{ base: '2', laptop: '8' }}
              mt={{ base: '8', laptop: '24' }}
            >
              <CheckMark size="12" />
              <Text
                ml="4"
                textStyle={{ base: 'h6Headline', laptop: 'h4Headline' }}
                textAlign="center"
              >
                {t('SALES-CONVERSATION_OFFERS_FOOTER_TITLE')}
              </Text>
            </Center>
          )}
        </>
      ) : (
        <Center
          mx={{ base: '2', laptop: '8' }}
          mt={{ base: '4', laptop: '24' }}
        >
          <IconActionClose size="80" color={colors.gray[800]} />
          <Text ml="4" textStyle={{ base: 'h6Headline', laptop: 'h4Headline' }}>
            {t('No offers found')}
          </Text>
        </Center>
      )}
    </SimpleGrid>
  );
};

export default OffersContainerContent;
